import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { Calendar, Search, Sparkles, Clock, PartyPopper } from 'lucide-react';

const EmployeeHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/holidays?year=${selectedYear}&t=${Date.now()}`);
      setHolidays(res.data);
    } catch (err) {
      console.error('Failed to fetch holidays:', err);
    } finally {
      setLoading(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filteredHolidays = holidays.filter(h =>
    h.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const upcomingHolidays = filteredHolidays.filter(h => new Date(h.date) >= today);
  const nextHoliday = upcomingHolidays.length > 0 ? upcomingHolidays[0] : null;

  const getDaysLeft = (targetDate) => {
    const d = new Date(targetDate);
    d.setHours(0, 0, 0, 0);
    const diffTime = d.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today!';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 rounded-3xl p-6 sm:p-8 text-white shadow-lg relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="p-2 bg-white/20 backdrop-blur-md rounded-xl inline-flex text-white">
                <PartyPopper className="w-5 h-5" />
              </span>
              <span className="text-xs uppercase font-bold tracking-widest text-purple-200">Company Calendar</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Official Holidays {selectedYear}</h1>
            <p className="text-purple-100 text-sm mt-1 max-w-xl">
              Plan your vacations and time-off with our official company holiday calendar. You will receive an automated notification 1 day prior to each holiday!
            </p>
          </div>

          {nextHoliday && (
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 sm:p-5 flex items-center gap-4 shrink-0 shadow-inner">
              <div className="w-12 h-12 rounded-xl bg-white text-purple-600 flex flex-col items-center justify-center font-bold shadow-md">
                <span className="text-[10px] uppercase tracking-wider font-extrabold leading-none text-purple-400">
                  {new Date(nextHoliday.date).toLocaleDateString(undefined, { month: 'short' })}
                </span>
                <span className="text-lg leading-tight text-purple-700">
                  {new Date(nextHoliday.date).getDate()}
                </span>
              </div>
              <div>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-emerald-400/20 text-emerald-300 px-2 py-0.5 rounded-full mb-1">
                  <Sparkles className="w-3 h-3" /> Next Holiday
                </span>
                <h4 className="font-bold text-white text-base leading-snug">{nextHoliday.name}</h4>
                <p className="text-xs text-purple-200 flex items-center gap-1 mt-0.5">
                  <Clock className="w-3.5 h-3.5" /> {getDaysLeft(nextHoliday.date)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search holiday by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
          />
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          <label className="text-xs font-semibold text-gray-500 uppercase">Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-medium text-gray-700 bg-white"
          >
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>
        </div>
      </div>

      {/* Holidays Grid */}
      {loading ? (
        <div className="p-12 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
          <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          Loading company holidays...
        </div>
      ) : filteredHolidays.length === 0 ? (
        <div className="p-12 text-center text-gray-500 bg-white rounded-2xl border border-gray-100">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-base font-semibold text-gray-700">No holidays found</p>
          <p className="text-xs text-gray-400 mt-1">Try adjusting your search or year filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredHolidays.map((holiday) => {
            const hDate = new Date(holiday.date);
            const isPast = hDate < today;
            const isUpcoming = hDate >= today;

            return (
              <div
                key={holiday._id}
                className={`p-5 rounded-2xl border transition-all duration-200 flex items-start gap-4 ${
                  isPast
                    ? 'bg-gray-50/60 border-gray-200 opacity-60'
                    : 'bg-white border-purple-100 hover:border-purple-300 hover:shadow-md'
                }`}
              >
                <div
                  className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center shrink-0 font-bold shadow-sm ${
                    isPast
                      ? 'bg-gray-200 text-gray-600'
                      : 'bg-purple-50 border border-purple-200 text-purple-700'
                  }`}
                >
                  <span className="text-[11px] uppercase tracking-wider font-extrabold leading-none opacity-80">
                    {hDate.toLocaleDateString(undefined, { month: 'short' })}
                  </span>
                  <span className="text-xl leading-tight font-black">{hDate.getDate()}</span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        isPast
                          ? 'bg-gray-200 text-gray-600'
                          : 'bg-purple-100 text-purple-700'
                      }`}
                    >
                      {hDate.toLocaleDateString(undefined, { weekday: 'long' })}
                    </span>
                    {isUpcoming && (
                      <span className="text-[11px] font-medium text-emerald-600">
                        {getDaysLeft(holiday.date)}
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 text-base truncate">{holiday.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {hDate.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EmployeeHolidays;
